import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Send,
  Building,
  AlertTriangle,
  Shield,
  HardHat,
  FileText,
  Info,
  Calculator,
  Percent
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Norwegian employer costs percentages (standard rates)
const EMPLOYER_COSTS = {
  feriepenger: 10.8,    // Holiday pay
  pensjon: 2.0,         // Pension
  forsikring: 2.0,      // Insurance
  arbeidsgiveravgift: 14.1  // Employer's social security
};

export default function AdminProjectApprovalV2() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [newMessage, setNewMessage] = useState("");
  const [proposedPrice, setProposedPrice] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Approval state
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalData, setApprovalData] = useState({
    approved_budget: "",
    commission_percent: 10,
    notes: ""
  });
  const [approving, setApproving] = useState(false);
  
  // Rejection state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchProject();
    
    // Auto-refresh messages every 5 seconds
    const interval = setInterval(() => {
      fetchProject();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/admin/projects/${projectId}/review`);
      setProject(res.data);
      setClientInfo(res.data.client_info);
      setMessages(res.data.negotiation_messages || []);
      
      // Set initial approval budget
      setApprovalData(prev => ({
        ...prev,
        approved_budget: res.data.proposed_budget || ""
      }));
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/admin/project-approval");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/admin/projects/${projectId}/negotiate`, {
        message: newMessage.trim(),
        proposed_price: proposedPrice ? parseFloat(proposedPrice) : null
      });
      
      setNewMessage("");
      setProposedPrice("");
      fetchProject(); // Refresh messages
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Calculate employer costs breakdown
  const calculateEmployerCosts = (approvedBudget, platformFee) => {
    if (!approvedBudget || isNaN(parseFloat(approvedBudget))) return null;
    
    const budget = parseFloat(approvedBudget);
    const platformAmount = budget * (platformFee / 100);
    const afterPlatform = budget - platformAmount;
    
    const feriepenger = afterPlatform * (EMPLOYER_COSTS.feriepenger / 100);
    const pensjon = afterPlatform * (EMPLOYER_COSTS.pensjon / 100);
    const forsikring = afterPlatform * (EMPLOYER_COSTS.forsikring / 100);
    const arbeidsgiveravgift = afterPlatform * (EMPLOYER_COSTS.arbeidsgiveravgift / 100);
    
    const totalEmployerCosts = feriepenger + pensjon + forsikring + arbeidsgiveravgift;
    const netForWorkers = afterPlatform - totalEmployerCosts;
    
    return {
      platformAmount,
      afterPlatform,
      feriepenger,
      pensjon,
      forsikring,
      arbeidsgiveravgift,
      totalEmployerCosts,
      netForWorkers
    };
  };

  // Debounced budget update - saves to DB when admin edits the budget live
  const handleUpdateBudget = async (newBudget) => {
    if (!newBudget || isNaN(parseFloat(newBudget))) return;
    
    try {
      await axios.post(`${API}/admin/projects/${projectId}/update-budget`, {
        approved_budget: parseFloat(newBudget)
      });
      // Silent update - no toast to avoid spam
    } catch (error) {
      console.error("Failed to update budget:", error);
    }
  };

  // Update platform fee
  const handleUpdatePlatformFee = async (newFee) => {
    if (!newFee || isNaN(parseFloat(newFee))) return;
    
    try {
      await axios.post(`${API}/admin/projects/${projectId}/update-platform-fee`, {
        platform_fee: parseFloat(newFee)
      });
    } catch (error) {
      console.error("Failed to update platform fee:", error);
    }
  };

  const handleApprove = async () => {
    if (!approvalData.approved_budget) {
      toast.error("Please enter the approved budget");
      return;
    }
    
    setApproving(true);
    try {
      const commission = parseFloat(approvalData.commission_percent) || 25;
      const budget = parseFloat(approvalData.approved_budget);
      const netForWorkers = budget - (budget * commission / 100);
      
      await axios.post(`${API}/admin/projects/${projectId}/approve`, {
        approved_budget: budget,
        commission_percent: commission,
        net_amount_for_workers: netForWorkers,
        notes: approvalData.notes
      });
      
      toast.success("Project approved! Contract created for client.");
      navigate("/admin/project-approval");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve project");
    } finally {
      setApproving(false);
      setShowApprovalDialog(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/projects/${projectId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Project rejected");
      navigate("/admin/project-approval");
    } catch (error) {
      toast.error("Failed to reject project");
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Approval">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Approval">
        <div className="text-center py-12">
          <p className="text-[#94A3B8]">Project not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const netForWorkers = approvalData.approved_budget 
    ? parseFloat(approvalData.approved_budget) - (parseFloat(approvalData.approved_budget) * parseFloat(approvalData.commission_percent) / 100)
    : 0;

  return (
    <DashboardLayout navItems={adminNavItems} title="Project Approval">
      <div className="space-y-6" data-testid="admin-project-approval">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => navigate("/admin/project-approval")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{project.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${
                  project.status === "negotiating" 
                    ? "bg-blue-500/20 text-blue-400" 
                    : "bg-yellow-500/20 text-yellow-400"
                }`}>
                  {project.status === "negotiating" ? "Negotiating" : "Pending Approval"}
                </Badge>
                <span className="text-[#94A3B8] text-sm">
                  Submitted {new Date(project.submitted_at || project.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={() => setShowRejectDialog(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
              onClick={() => setShowApprovalDialog(true)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Project Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Info Card */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#23F7DD]" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-[#94A3B8]">{project.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-xs text-[#94A3B8]">Location</p>
                    <p className="text-white flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-[#23F7DD]" />
                      {project.location_city}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Type</p>
                    <p className="text-white capitalize">{project.project_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Complexity</p>
                    <p className="text-white capitalize">{project.complexity}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Crew Size</p>
                    <p className="text-white flex items-center gap-1">
                      <Users className="w-4 h-4 text-[#23F7DD]" />
                      {project.crew_size || "Not specified"}
                    </p>
                  </div>
                </div>

                {project.start_date && (
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-[#94A3B8]">Requested Start Date</p>
                    <p className="text-white flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-[#23F7DD]" />
                      {new Date(project.start_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Budget Card - Editable */}
            <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                  Budget Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Client's Original Proposal */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-[#94A3B8]">Client's Original Proposal</p>
                  <p className="text-xl font-bold text-white">
                    {project.proposed_budget?.toLocaleString()} NOK
                  </p>
                </div>

                {/* Editable Approved Budget */}
                <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-[#94A3B8]">Approved Budget (Editable)</p>
                    <Badge className="bg-blue-500/20 text-blue-400">Live Edit</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={approvalData.approved_budget}
                      onChange={(e) => {
                        setApprovalData({...approvalData, approved_budget: e.target.value});
                        handleUpdateBudget(e.target.value);
                      }}
                      className="bg-black/50 border-[#23F7DD]/50 text-[#23F7DD] text-2xl font-bold h-14"
                      placeholder="Enter approved amount"
                    />
                    <span className="text-xl text-[#23F7DD] font-bold">NOK</span>
                  </div>
                </div>

                {/* Norwegian Employer Costs */}
                {approvalData.approved_budget && (() => {
                  const costs = calculateEmployerCosts(approvalData.approved_budget, approvalData.commission_percent);
                  if (!costs) return null;
                  
                  return (
                    <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-4">
                        <Calculator className="w-5 h-5 text-orange-400" />
                        <span className="text-orange-400 font-medium">Norwegian Employer Costs</span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {/* Platform Fee - Editable */}
                        <div className="flex justify-between items-center p-2 bg-purple-500/10 rounded border border-purple-500/30">
                          <span className="text-purple-400 font-medium">Platform Fee (Editable)</span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={approvalData.commission_percent}
                              onChange={(e) => {
                                setApprovalData({...approvalData, commission_percent: e.target.value});
                                handleUpdatePlatformFee(e.target.value);
                              }}
                              className="w-20 h-8 bg-black/50 border-purple-500/50 text-purple-400 text-center font-bold"
                            />
                            <span className="text-purple-400">%</span>
                            <span className="text-[#94A3B8]">({costs.platformAmount.toLocaleString()} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Feriepenger */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Feriepenger (Holiday Pay)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.feriepenger}%</span>
                            <span className="text-[#94A3B8] ml-2">({costs.feriepenger.toLocaleString()} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Pensjon */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Pensjon (Pension)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.pensjon}%</span>
                            <span className="text-[#94A3B8] ml-2">({costs.pensjon.toLocaleString()} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Forsikring */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Forsikring (Insurance)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.forsikring}%</span>
                            <span className="text-[#94A3B8] ml-2">({costs.forsikring.toLocaleString()} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Arbeidsgiveravgift */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Arbeidsgiveravgift (Social Security)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.arbeidsgiveravgift}%</span>
                            <span className="text-[#94A3B8] ml-2">({costs.arbeidsgiveravgift.toLocaleString()} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Total Employer Costs */}
                        <div className="flex justify-between items-center p-3 bg-orange-500/10 rounded border border-orange-500/30 mt-2">
                          <span className="text-orange-400 font-medium">Total Employer Costs</span>
                          <span className="text-orange-400 font-bold">
                            {(costs.totalEmployerCosts + costs.platformAmount).toLocaleString()} NOK
                          </span>
                        </div>
                        
                        {/* Net for Workers */}
                        <div className="flex justify-between items-center p-3 bg-[#23F7DD]/10 rounded border border-[#23F7DD]/30">
                          <span className="text-[#23F7DD] font-medium">Net for Workers (TL/WRK)</span>
                          <span className="text-[#23F7DD] font-bold text-lg">
                            {costs.netForWorkers.toLocaleString()} NOK
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {project.waiting_time_terms?.enabled && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-orange-400" />
                      <span className="text-orange-400 font-medium">Waiting Time Terms Included</span>
                    </div>
                    <p className="text-sm text-[#94A3B8]">
                      Max {project.waiting_time_terms.max_hours_per_person} hours/person @ {project.waiting_time_terms.hourly_rate} NOK/hr
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SJA Card */}
            {project.sja_form && (
              <Card className="bg-[#0A0A0A] border-red-500/30">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Safety Job Analysis (SJA)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.sja_form.hazards?.length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Identified Hazards:</p>
                      <div className="space-y-2">
                        {project.sja_form.hazards.map((hazard, idx) => (
                          <div key={idx} className="p-3 bg-black/30 rounded-lg flex items-start gap-3">
                            <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                              hazard.risk_level === "high" ? "text-red-400" :
                              hazard.risk_level === "medium" ? "text-yellow-400" : "text-green-400"
                            }`} />
                            <div>
                              <p className="text-white font-medium">{hazard.hazard}</p>
                              <p className="text-sm text-[#94A3B8]">
                                Risk: <span className={`font-medium ${
                                  hazard.risk_level === "high" ? "text-red-400" :
                                  hazard.risk_level === "medium" ? "text-yellow-400" : "text-green-400"
                                }`}>{hazard.risk_level}</span>
                              </p>
                              {hazard.mitigation && (
                                <p className="text-sm text-[#94A3B8]">Mitigation: {hazard.mitigation}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {project.sja_form.ppe_required?.length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Required PPE:</p>
                      <div className="flex flex-wrap gap-2">
                        {project.sja_form.ppe_required.map((ppe, idx) => (
                          <Badge key={idx} className="bg-red-500/20 text-red-300">
                            <HardHat className="w-3 h-3 mr-1" />
                            {ppe}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Client & Chat */}
          <div className="space-y-6">
            {/* Client Info */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <Building className="w-5 h-5 text-purple-400" />
                  Client
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-white font-medium">{project.client_name}</p>
                    {project.client_company && (
                      <p className="text-sm text-[#94A3B8]">{project.client_company}</p>
                    )}
                  </div>
                  {clientInfo?.email && (
                    <p className="text-sm text-[#94A3B8]">{clientInfo.email}</p>
                  )}
                  {clientInfo?.phone && (
                    <p className="text-sm text-[#94A3B8]">{clientInfo.phone}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Negotiation Chat */}
            <Card className="bg-[#0A0A0A] border-blue-500/30">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                  Negotiation Chat
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Messages */}
                <div className="h-[300px] overflow-y-auto mb-4 space-y-3 p-3 bg-black/30 rounded-lg">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-[#94A3B8] text-center">
                      <MessageSquare className="w-10 h-10 mb-2 opacity-50" />
                      <p>No messages yet</p>
                      <p className="text-xs">Start a conversation to negotiate the price</p>
                    </div>
                  ) : (
                    messages.map((msg, idx) => (
                      <div
                        key={msg.message_id || idx}
                        className={`flex ${msg.sender_role === "admin" ? "justify-end" : "justify-start"}`}
                      >
                        <div className={`max-w-[85%] p-3 rounded-lg ${
                          msg.sender_role === "admin"
                            ? "bg-[#23F7DD] text-[#050505]"
                            : "bg-white/10 text-white"
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium opacity-70">
                              {msg.sender_role === "admin" ? "You" : msg.sender_name}
                            </span>
                            <span className="text-xs opacity-50">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-sm">{msg.message}</p>
                          {msg.proposed_price && (
                            <div className={`mt-2 p-2 rounded ${
                              msg.sender_role === "admin" ? "bg-black/20" : "bg-[#23F7DD]/20"
                            }`}>
                              <p className="text-xs font-medium">Proposed Price:</p>
                              <p className="font-bold">{msg.proposed_price.toLocaleString()} NOK</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Counter-offer (NOK)"
                      value={proposedPrice}
                      onChange={(e) => setProposedPrice(e.target.value)}
                      className="w-32 bg-black/50 border-white/10 text-white text-sm"
                    />
                    <span className="text-[#94A3B8] self-center text-xs">Optional</span>
                  </div>
                  <div className="flex gap-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="bg-black/50 border-white/10 text-white resize-none text-sm"
                      rows={2}
                    />
                    <Button
                      type="submit"
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Approval Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Unbounded'] flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                Approve Project
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <p className="text-sm text-yellow-300">
                    Approving will create a contract for the client to sign. The project will go live after the client signs.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Approved Budget (NOK) *</Label>
                <Input
                  type="number"
                  value={approvalData.approved_budget}
                  onChange={(e) => setApprovalData({...approvalData, approved_budget: e.target.value})}
                  placeholder="Final agreed budget"
                  className="bg-black/50 border-white/10 text-white text-lg"
                />
                <p className="text-xs text-[#94A3B8]">
                  Client proposed: {project.proposed_budget?.toLocaleString()} NOK
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Platform Commission (%)</Label>
                <Input
                  type="number"
                  value={approvalData.commission_percent}
                  onChange={(e) => setApprovalData({...approvalData, commission_percent: e.target.value})}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>

              {approvalData.approved_budget && (() => {
                const approvedBudget = parseFloat(approvalData.approved_budget);
                const platformPercent = parseFloat(approvalData.commission_percent);
                const platformAmount = approvedBudget * (platformPercent / 100);
                const afterPlatform = approvedBudget - platformAmount;
                
                // Calculate Norwegian employer costs
                const feriepenger = afterPlatform * (EMPLOYER_COSTS.feriepenger / 100);
                const pensjon = afterPlatform * (EMPLOYER_COSTS.pensjon / 100);
                const forsikring = afterPlatform * (EMPLOYER_COSTS.forsikring / 100);
                const arbeidsgiveravgift = afterPlatform * (EMPLOYER_COSTS.arbeidsgiveravgift / 100);
                const totalEmployerCosts = feriepenger + pensjon + forsikring + arbeidsgiveravgift;
                const netForWorkers = afterPlatform - totalEmployerCosts;
                
                return (
                  <div className="space-y-4">
                    {/* Current Negotiated Budget */}
                    <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                      <p className="text-sm text-[#94A3B8]">Approved Budget</p>
                      <p className="text-2xl font-bold text-[#23F7DD]">
                        {approvedBudget.toLocaleString()} NOK
                      </p>
                    </div>

                    {/* Norwegian Employer Costs Breakdown */}
                    <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-4">
                        <Calculator className="w-5 h-5 text-orange-400" />
                        <span className="text-orange-400 font-medium">Norwegian Employer Costs</span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {/* Platform Fee */}
                        <div className="flex justify-between items-center p-2 bg-purple-500/10 rounded border border-purple-500/30">
                          <span className="text-purple-400 font-medium">Platform Fee</span>
                          <div className="flex items-center gap-2">
                            <span className="text-purple-400">{platformPercent}%</span>
                            <span className="text-[#94A3B8]">({platformAmount.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Feriepenger */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Feriepenger (Holiday Pay)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.feriepenger}%</span>
                            <span className="text-[#94A3B8] ml-2">({feriepenger.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Pensjon */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Pensjon (Pension)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.pensjon}%</span>
                            <span className="text-[#94A3B8] ml-2">({pensjon.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Forsikring */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Forsikring (Insurance)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.forsikring}%</span>
                            <span className="text-[#94A3B8] ml-2">({forsikring.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Arbeidsgiveravgift */}
                        <div className="flex justify-between items-center p-2 bg-black/30 rounded">
                          <span className="text-[#94A3B8]">Arbeidsgiveravgift (Social Security)</span>
                          <div className="text-right">
                            <span className="text-white font-semibold">{EMPLOYER_COSTS.arbeidsgiveravgift}%</span>
                            <span className="text-[#94A3B8] ml-2">({arbeidsgiveravgift.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK)</span>
                          </div>
                        </div>
                        
                        {/* Total Employer Costs */}
                        <div className="flex justify-between items-center p-3 bg-orange-500/10 rounded border border-orange-500/30 mt-2">
                          <span className="text-orange-400 font-medium">Total Employer Costs</span>
                          <span className="text-orange-400 font-bold">
                            {totalEmployerCosts.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK
                          </span>
                        </div>
                        
                        {/* Net for Workers (TL/WRK) */}
                        <div className="flex justify-between items-center p-3 bg-[#23F7DD]/10 rounded border border-[#23F7DD]/30 mt-2">
                          <span className="text-[#23F7DD] font-medium">Net for Workers (TL/WRK)</span>
                          <span className="text-[#23F7DD] font-bold text-lg">
                            {netForWorkers.toLocaleString(undefined, {maximumFractionDigits: 0})} NOK
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Notes (Optional)</Label>
                <Textarea
                  value={approvalData.notes}
                  onChange={(e) => setApprovalData({...approvalData, notes: e.target.value})}
                  placeholder="Any notes for internal use..."
                  className="bg-black/50 border-white/10 text-white"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                className="border-white/20 text-white"
                onClick={() => setShowApprovalDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-500 hover:bg-green-600 text-white font-bold"
                onClick={handleApprove}
                disabled={approving || !approvalData.approved_budget}
              >
                {approving ? "Approving..." : "Approve & Create Contract"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Unbounded'] flex items-center gap-2">
                <XCircle className="w-6 h-6 text-red-400" />
                Reject Project
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Reason for Rejection *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why the project is being rejected..."
                  className="bg-black/50 border-white/10 text-white"
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                className="border-white/20 text-white"
                onClick={() => setShowRejectDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white font-bold"
                onClick={handleReject}
                disabled={!rejectReason.trim()}
              >
                Reject Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
